import z from 'zod'

import { EnumBuilder, type ZodInfer } from '@mntn-dev/utility-types'

import { ModelFilterSchema } from '../model-filter-schema.ts'
import { UbsInvoiceId, UbsInvoiceIdSchema } from './finance/ubs-invoice-id.ts'
import {
  UbsInvoiceStatus,
  UbsInvoiceStatusEnum,
  UbsInvoiceStatuses,
  UbsInvoiceStatusSchema,
} from './finance/ubs-invoice-status.ts'
import {
  DollarsSchema,
  IsoDateTimeStringSchema,
  NonEmptyStringSchema,
} from './property.models.ts'
import { InvoiceIdSchema, ProjectIdSchema } from './unique-id.models.ts'

/**
 * Invoice Types
 */
export const [InvoiceTypes, InvoiceTypeSchema, InvoiceTypeEnum] = EnumBuilder(
  'charge',
  'payment',
  'advance-payment'
)

export type InvoiceType = ZodInfer<typeof InvoiceTypeSchema>

export const [PaymentTypes, PaymentTypeSchema] = EnumBuilder(
  'normal',
  'advance'
)

export type PaymentType = ZodInfer<typeof PaymentTypeSchema>

export const [InvoiceStatuses, InvoiceStatusSchema, InvoiceStatusEnum] =
  EnumBuilder(
    'pending',
    'approved',
    'paid',
    'voided',
    'refunded',
    'rejected',
    'canceled'
  )

export type InvoiceStatus = ZodInfer<typeof InvoiceStatusSchema>

/**
 * External Invoice Tracking System
 */
export const [
  ExternalInvoiceTrackingSystems,
  ExternalInvoiceTrackingSystemSchema,
  ExternalInvoiceTrackingSystemEnum,
] = EnumBuilder('ubs')

export type ExternalInvoiceTrackingSystem = ZodInfer<
  typeof ExternalInvoiceTrackingSystemSchema
>

/**
 * External Invoice Tracking ID
 */
export const ExternalInvoiceTrackingIdSchema = UbsInvoiceIdSchema
export type ExternalInvoiceTrackingId = ZodInfer<
  typeof ExternalInvoiceTrackingIdSchema
>
export const ExternalInvoiceTrackingId = UbsInvoiceId

/**
 * External Invoice Tracking Status
 */
export const ExternalInvoiceTrackingStatusSchema = UbsInvoiceStatusSchema
export type ExternalInvoiceTrackingStatus = ZodInfer<
  typeof ExternalInvoiceTrackingStatusSchema
>

export const ExternalInvoiceTrackingStatus = UbsInvoiceStatus

export const ExternalInvoiceTrackingStatusEnum = UbsInvoiceStatusEnum

export const ExternalInvoiceTrackingStatuses = UbsInvoiceStatuses

/**
 * Charge Invoice Details Schema
 */
export const ChargeInvoiceDetailsSchema = z.object({
  invoiceType: z.literal('charge'),
  metadata: z.strictObject({}),
})

export type ChargeInvoiceDetails = ZodInfer<typeof ChargeInvoiceDetailsSchema>

/**
 * Payment Invoice Details Schema
 */
export const PaymentInvoiceDetailsSchema = z.object({
  invoiceType: z.literal('payment'),
  metadata: z.strictObject({}),
})

export type PaymentInvoiceDetails = ZodInfer<typeof PaymentInvoiceDetailsSchema>

/**
 * Advance Payment Invoice Details Schema
 */
export const AdvancePaymentInvoiceDetailsSchema = z.object({
  invoiceType: z.literal('advance-payment'),
  metadata: z
    .strictObject({
      needed_by_date: IsoDateTimeStringSchema,
      production_or_shoot_date: z.unknown().optional(), // Deprecated, may be present in older data
      reason: NonEmptyStringSchema(),
      fee_exemption: z.boolean(),
      over_limit_exception: z.boolean(),
      multiple_advances_exception: z.boolean(),
      advance_percent: z.number().min(0).max(100),
      maker_payout_total: DollarsSchema(),
    })
    // Redact the deprecated production_or_shoot_date field
    .transform(({ production_or_shoot_date, ...metadata }) => metadata),
})

export type AdvancePaymentInvoiceDetails = ZodInfer<
  typeof AdvancePaymentInvoiceDetailsSchema
>

/**
 * Invoice Details Schema
 */
export const InvoiceDetailsSchema = {
  charge: ChargeInvoiceDetailsSchema,
  payment: PaymentInvoiceDetailsSchema,
  'advance-payment': AdvancePaymentInvoiceDetailsSchema,
}

/**
 * Invoice Metadata
 */
export type InvoiceMetadata =
  | ZodInfer<typeof ChargeInvoiceDetailsSchema.shape.metadata>
  | ZodInfer<typeof PaymentInvoiceDetailsSchema.shape.metadata>
  | ZodInfer<typeof AdvancePaymentInvoiceDetailsSchema.shape.metadata>

/**
 * Invoice Domain Select Model Schema
 */
export const InvoiceDomainSelectModelSchema = z.object({
  invoiceId: InvoiceIdSchema,
  projectId: ProjectIdSchema,
  amount: DollarsSchema(),
  description: NonEmptyStringSchema(),
  status: InvoiceStatusSchema,
  details: z.discriminatedUnion('invoiceType', [
    ChargeInvoiceDetailsSchema,
    PaymentInvoiceDetailsSchema,
    AdvancePaymentInvoiceDetailsSchema,
  ]),
  tracking: z
    .object({
      system: ExternalInvoiceTrackingSystemSchema,
      id: ExternalInvoiceTrackingIdSchema,
      status: ExternalInvoiceTrackingStatusSchema,
    })
    .optional(),
})

export type InvoiceDomainSelectModel = ZodInfer<
  typeof InvoiceDomainSelectModelSchema
>

export type InvoiceDetailsByType<
  Type extends InvoiceType,
  TInvoice extends InvoiceDomainSelectModel = InvoiceDomainSelectModel,
> = Extract<TInvoice['details'], { invoiceType: Type }>

export type InvoiceDomainSelectModelByType<
  Type extends InvoiceType,
  TInvoice extends InvoiceDomainSelectModel = InvoiceDomainSelectModel,
> = TInvoice & { details: InvoiceDetailsByType<Type, TInvoice> }

export type ChargeInvoiceModel<
  TInvoice extends InvoiceDomainSelectModel = InvoiceDomainSelectModel,
> = InvoiceDomainSelectModelByType<'charge', TInvoice>

export type PaymentInvoiceModel<
  TInvoice extends InvoiceDomainSelectModel = InvoiceDomainSelectModel,
> = InvoiceDomainSelectModelByType<'payment', TInvoice>

export type AdvancePaymentInvoiceModel<
  TInvoice extends InvoiceDomainSelectModel = InvoiceDomainSelectModel,
> = InvoiceDomainSelectModelByType<'advance-payment', TInvoice>

export const isInvoiceType = <
  Type extends InvoiceType,
  TInvoice extends InvoiceDomainSelectModel,
>(
  invoice: TInvoice,
  invoiceType: Type
): invoice is InvoiceDomainSelectModelByType<Type, TInvoice> =>
  invoice.details.invoiceType === invoiceType

export const assertInvoiceType = <
  Type extends InvoiceType,
  TInvoice extends InvoiceDomainSelectModel,
>(
  invoice: TInvoice,
  invoiceType: Type
): InvoiceDomainSelectModelByType<Type, TInvoice> => {
  if (!isInvoiceType(invoice, invoiceType)) {
    throw new Error(`Invoice is not of type ${invoiceType}`)
  }
  return invoice
}

/**
 * Invoice Domain Insert Model Schema
 */
export const InvoiceDomainInsertModelSchema =
  InvoiceDomainSelectModelSchema.partial({
    invoiceId: true,
  })

export type InvoiceDomainInsertModel = ZodInfer<
  typeof InvoiceDomainInsertModelSchema
>

/**
 * Invoice Domain Update Model Schema
 */
export const InvoiceDomainUpdateModelSchema =
  InvoiceDomainSelectModelSchema.pick({
    status: true,
    tracking: true,
  }).partial()

export type InvoiceDomainUpdateModel = ZodInfer<
  typeof InvoiceDomainUpdateModelSchema
>

/**
 * Invoice Domain Select Model Filters Schema
 */
export const InvoiceDomainSelectModelFiltersSchema = z
  .object({
    invoiceId: ModelFilterSchema(InvoiceIdSchema),
    projectId: ModelFilterSchema(ProjectIdSchema),
    status: ModelFilterSchema(InvoiceStatusSchema),
    invoiceType: ModelFilterSchema(InvoiceTypeSchema),
    tracking: z
      .object({
        system: ModelFilterSchema(ExternalInvoiceTrackingSystemSchema),
        id: ModelFilterSchema(ExternalInvoiceTrackingIdSchema),
        status: ModelFilterSchema(ExternalInvoiceTrackingStatusSchema),
      })
      .partial(),
  })
  .partial()

export type InvoiceDomainSelectModelFilters = ZodInfer<
  typeof InvoiceDomainSelectModelFiltersSchema
>
